import type {
  GetListParams,
  GetListResult,
  GetOneResult,
  CreateResult,
  UpdateResult,
  DeleteResult,
  GetOneParams,
  UpdateParams,
  DeleteParams,
} from "ra-core";
import type { PersonService } from "@domain/port/PersonService";
import { toRaPerson, type PersonRaRecord } from "./Mappers";
import type { PageQuery } from "@/domain/port/PageQuery";
import { asPersonId } from "@/domain/model/Ids";
import type { UpdatePersonCommand } from "@/domain/model/Person";

export function makePersonsAdapter(persons: PersonService) {
  return {
    async getList(
      _resource: string,
      params: GetListParams,
    ): Promise<GetListResult<PersonRaRecord>> {
      const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
      const { field, order } = params.sort ?? {
        field: "createdAt",
        order: "DESC",
      };
      const filter = params.filter ?? {};
      const q: PageQuery = {
        page,
        pageSize: perPage,
        sortBy: field,
        sortDir: order.toLowerCase() as "asc" | "desc",
        filter,
      };
      const res = await persons.list(q);
      return { data: res.items.map(toRaPerson), total: res.total };
    },

    async create(
      _resource: string,
      { data }: { data: any },
    ): Promise<CreateResult<PersonRaRecord>> {
      const created = await persons.create({
        displayName: data.displayName,
        preferredName: data.preferredName ?? null,
        mobilePhoneE164: data.mobilePhoneE164 ?? null,
        email: data.email ?? null,
        canAppearInPhoto: !!data.canAppearInPhoto,
      });
      return { data: toRaPerson(created) };
    },

    async getOne(
      _resource: string,
      params: GetOneParams,
    ): Promise<GetOneResult<PersonRaRecord>> {
      const item = await persons.getById(asPersonId(params.id));
      return { data: toRaPerson(item) };
    },

    async update(
      _resource: string,
      params: UpdateParams,
    ): Promise<UpdateResult<PersonRaRecord>> {
      const updated = await persons.update(
        asPersonId(params.id),
        params.data as UpdatePersonCommand,
      );
      return { data: toRaPerson(updated) };
    },

    async delete(
      _resource: string,
      params: DeleteParams,
    ): Promise<DeleteResult<PersonRaRecord>> {
      const deleted = await persons.delete(asPersonId(params.id));
      return { data: toRaPerson(deleted) };
    },
  };
}
